import React, { useState, useEffect } from 'react';
import { Dialog, Transition, Tab } from '@headlessui/react';
import { Fragment } from 'react';
import { 
  XMarkIcon, 
  LinkIcon, 
  QrCodeIcon, 
  ChatBubbleLeftRightIcon,
  CheckIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { fetchConversations, sendMessage } from '../../store/slices/messagesSlice';
import Avatar from './Avatar';
import { useNavigate } from 'react-router-dom';

import { userAPI } from '../../services/api';
import { User } from '../../types';
import useBackButton from '../../hooks/useBackButton';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  url: string;
  type: 'profile' | 'portfolio' | 'collaboration' | 'opportunity';
  data: any; // Metadata for the card
}

const ShareModal: React.FC<ShareModalProps> = ({ 
  isOpen, 
  onClose, 
  title, 
  url, 
  type,
  data 
}) => {
  useBackButton(onClose, isOpen, 150);
  const [copied, setCopied] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  
  const performSearch = async (query: string) => {
    if (!query.trim()) {
        setSearchResults([]);
        return;
    }
    try {
        const response = await userAPI.searchUsers(query);
        setSearchResults(response.data.data || []);
    } catch (error) {
        console.error('Search failed:', error);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
        performSearch(query);
    }, 300);
  };
  
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { conversations, loading } = useAppSelector(state => state.messages);
  const { user: currentUser } = useAppSelector(state => state.auth);

  useEffect(() => {
    return () => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
    };
  }, []);

  useEffect(() => {
    if (isOpen && currentUser && conversations.length === 0) {
      dispatch(fetchConversations({ page: 1 }));
    }
  }, [isOpen, dispatch, conversations.length, currentUser]);

  const handleCopy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSendToChat = async () => {
    if (!selectedUser || !currentUser) return;
    
    setSending(true);
    try {
      // Construct the rich message content
      const messageContent = JSON.stringify({
        type: type,
        id: data.id,
        title: title,
        image: data.image || data.profile_pic || data.cover_image || (data.images && data.images[0]) || data.thumbnail_image_url,
        subtitle: data.subtitle || data.role || data.user_type,
        url: url,
        // Add specific fields for opportunities
        ...(type === 'opportunity' && {
            location: data.location,
            is_paid: data.is_paid,
            budget: data.budget,
            compensation_type: data.compensation_type
        }),
        // Add specific fields for collaborations
        ...(type === 'collaboration' && {
            status: data.status,
            place: data.place,
            is_paid: data.is_paid,
            budget: data.budget,
            collaborators: data.collaborators?.slice(0, 3).map((c: any) => ({
                id: c.id,
                name: c.name,
                profile_pic: c.profile_pic || c.user?.profile_pic
            })),
            collaborator_count: data.collaborators?.length,
            creator_id: data.creator_id
        }),
        // Add specific fields for portfolio
        ...(type === 'portfolio' && {
            user_id: data.user_id
        })
      });

      await dispatch(sendMessage({
        recipient_id: selectedUser,
        content: messageContent,
        message_type: type // Custom type field in backend
      })).unwrap();

      toast.success('Sent to chat!');
      onClose();
    } catch (error) {
      console.error('Failed to send:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const filteredConversations = conversations.filter(conv => {
    if (!conv.other_user) return false;
    const name = `${conv.other_user.first_name} ${conv.other_user.last_name}`.toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

  // Determine available tabs and default index based on user state
  // Logged in: Chat (default), Link, QR Code
  // Guest: Link (only)
  
  const defaultIndex = currentUser ? 2 : 0; // 2 is Chat, 0 is Link

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900 flex justify-between items-center mb-4"
                >
                  <span>Share {type === 'profile' ? 'Profile' : type === 'portfolio' ? 'Work' : type === 'opportunity' ? 'Opportunity' : 'Collaboration'}</span>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </Dialog.Title>

                <Tab.Group defaultIndex={defaultIndex}>
                  <Tab.List className="flex space-x-1 rounded-xl bg-gray-100 p-1 mb-6">
                    <Tab
                      className={({ selected }) =>
                        `w-full rounded-lg py-2.5 text-sm font-medium leading-5 ring-white ring-opacity-60 ring-offset-2 ring-offset-teal-400 focus:outline-none focus:ring-2 ${
                          selected
                            ? 'bg-white text-teal-700 shadow'
                            : 'text-gray-500 hover:bg-white/[0.12] hover:text-gray-700'
                        }`
                      }
                    >
                      <div className="flex items-center justify-center gap-2">
                        <LinkIcon className="h-4 w-4" />
                        Link
                      </div>
                    </Tab>
                    
                    {currentUser && (
                      <>
                        <Tab
                          className={({ selected }) =>
                            `w-full rounded-lg py-2.5 text-sm font-medium leading-5 ring-white ring-opacity-60 ring-offset-2 ring-offset-teal-400 focus:outline-none focus:ring-2 ${
                              selected
                                ? 'bg-white text-teal-700 shadow'
                                : 'text-gray-500 hover:bg-white/[0.12] hover:text-gray-700'
                            }`
                          }
                        >
                          <div className="flex items-center justify-center gap-2">
                            <QrCodeIcon className="h-4 w-4" />
                            QR Code
                          </div>
                        </Tab>
                        <Tab
                          className={({ selected }) =>
                            `w-full rounded-lg py-2.5 text-sm font-medium leading-5 ring-white ring-opacity-60 ring-offset-2 ring-offset-teal-400 focus:outline-none focus:ring-2 ${
                              selected
                                ? 'bg-white text-teal-700 shadow'
                                : 'text-gray-500 hover:bg-white/[0.12] hover:text-gray-700'
                            }`
                          }
                        >
                          <div className="flex items-center justify-center gap-2">
                            <ChatBubbleLeftRightIcon className="h-4 w-4" />
                            Chat
                          </div>
                        </Tab>
                      </>
                    )}
                  </Tab.List>
                  <Tab.Panels>
                    {/* Link Tab */}
                    <Tab.Panel className="focus:outline-none">
                      <div className="bg-gray-50 p-4 rounded-xl flex items-center justify-between gap-3 border border-gray-200">
                        <p className="text-sm text-gray-600 truncate flex-1 font-mono">{url}</p>
                        <button
                          onClick={handleCopy}
                          className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-gray-600"
                          title="Copy Link"
                        >
                          {copied ? <CheckIcon className="h-5 w-5 text-green-500" /> : <LinkIcon className="h-5 w-5" />}
                        </button>
                      </div>
                    </Tab.Panel>

                    {/* QR Code Tab */}
                    <Tab.Panel className="focus:outline-none flex flex-col items-center justify-center py-4">
                      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                        <QRCodeSVG value={url} size={200} level="H" includeMargin={true} />
                      </div>
                      <p className="mt-4 text-sm text-gray-500">Scan to view on mobile</p>
                    </Tab.Panel>

                    {/* Chat Tab */}
                    <Tab.Panel className="focus:outline-none">
                      <div className="relative mb-4">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search for people..."
                          value={searchQuery}
                          onChange={handleSearchChange}
                          className="w-full pl-9 pr-4 py-2 bg-gray-50 border-none rounded-lg text-sm focus:ring-2 focus:ring-teal-500/20"
                        />
                      </div>
                      
                      <div className="h-48 overflow-y-auto space-y-1 mb-4 scrollbar-thin scrollbar-thumb-gray-200">
                        {/* Search Results */}
                        {searchQuery && searchResults.length > 0 && (
                            <div className="mb-2">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-2">Search Results</p>
                                {searchResults.map(user => (
                                <button
                                    key={user.id}
                                    onClick={() => setSelectedUser(user.id)}
                                    className={`w-full flex items-center p-2 rounded-lg transition-colors ${
                                    selectedUser === user.id 
                                        ? 'bg-teal-50 ring-1 ring-teal-200' 
                                        : 'hover:bg-gray-50'
                                    }`}
                                >
                                    <Avatar 
                                    src={user.profile_pic_thumb_signed || user.profile_pic} 
                                    alt={user.first_name} 
                                    size="sm" 
                                    className="mr-3"
                                    />
                                    <div className="flex-1 text-left">
                                    <p className="text-sm font-medium text-gray-900">
                                        {/* @ts-ignore */}
                                        {user.full_name || `${user.first_name} ${user.last_name}`}
                                    </p>
                                    <p className="text-xs text-gray-500 capitalize">{user.user_type}</p>
                                    </div>
                                    {selectedUser === user.id && (
                                    <CheckIcon className="h-5 w-5 text-teal-600" />
                                    )}
                                </button>
                                ))}
                            </div>
                        )}

                        {/* Recent Conversations */}
                        {!searchQuery && (
                            <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-2">Recent Conversations</p>
                                {conversations.map(conv => (
                                <button
                                    key={conv.conversation_id}
                                    onClick={() => setSelectedUser(conv.other_user.id)}
                                    className={`w-full flex items-center p-2 rounded-lg transition-colors ${
                                    selectedUser === conv.other_user.id 
                                        ? 'bg-teal-50 ring-1 ring-teal-200' 
                                        : 'hover:bg-gray-50'
                                    }`}
                                >
                                    <Avatar 
                                    src={conv.other_user.profile_pic_thumb_signed || conv.other_user.profile_pic} 
                                    alt={conv.other_user.first_name} 
                                    size="sm" 
                                    className="mr-3"
                                    />
                                    <div className="flex-1 text-left">
                                    <p className="text-sm font-medium text-gray-900">
                                        {conv.subject 
                                          ? `${conv.subject} - ${conv.other_user.first_name} ${conv.other_user.last_name}` 
                                          : `${conv.other_user.first_name} ${conv.other_user.last_name}`}
                                    </p>
                                    </div>
                                    {selectedUser === conv.other_user.id && (
                                    <CheckIcon className="h-5 w-5 text-teal-600" />
                                    )}
                                </button>
                                ))}
                            </div>
                        )}
                        
                        {searchQuery && searchResults.length === 0 && (
                            <p className="text-center text-sm text-gray-500 py-4">No users found</p>
                        )}
                         {!searchQuery && conversations.length === 0 && (
                            <p className="text-center text-sm text-gray-500 py-4">No recent conversations</p>
                        )}
                      </div>

                      <button
                        onClick={handleSendToChat}
                        disabled={!selectedUser || sending}
                        className="w-full py-2.5 bg-black text-white rounded-xl font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {sending ? (
                            <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Sending...
                            </>
                        ) : (
                            <>
                            <PaperAirplaneIcon className="h-4 w-4 -rotate-45 mb-1" />
                            Send
                            </>
                        )}
                      </button>
                    </Tab.Panel>
                  </Tab.Panels>
                </Tab.Group>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

// Helper icon for button
function PaperAirplaneIcon(props: React.ComponentProps<'svg'>) {
  return (
    <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
    </svg>
  );
}

export default ShareModal;
